import React, { useState, useEffect } from 'react';
import { X, Globe, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const PublishingModal = ({ isOpen, onClose, onPublish, site }) => {
  const [publishState, setPublishState] = useState('idle'); // idle, saving, translating, generating, complete, error
  const [currentStep, setCurrentStep] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [translationWarnings, setTranslationWarnings] = useState([]);

  const isMultilingual = site?.multilingual?.enabled && site?.languages?.length > 0;
  const languageCount = site?.languages?.filter(l => l.isActive)?.length || 0;

  const steps = [
    { key: 'saving', label: 'Saving changes', duration: 10 },
    { key: 'generating', label: 'Generating website files', duration: 20 },
    { key: 'complete', label: 'Publishing complete', duration: 10 }
  ];

  useEffect(() => {
    if (isOpen && publishState === 'idle') {
      startPublishing();
    }
  }, [isOpen]);

  const startPublishing = async () => {
    setPublishState('saving');
    setCurrentStep('Saving your changes...');
    setProgress(10);
    setError(null);

    // Simulate progress while publish is happening
    const progressSteps = [
      { state: 'saving', label: 'Saving your changes...', progress: 30 },
      { state: 'generating', label: 'Generating website files', progress: 90 }
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        const step = progressSteps[stepIndex];
        setPublishState(step.state);
        setCurrentStep(step.label);
        setProgress(step.progress);
        stepIndex++;
      }
    }, 1500); // Standard progress speed

    try {
      // Start the actual publish process
      const result = await onPublish();

      // Clear progress interval and complete
      clearInterval(progressInterval);
      setProgress(100);
      setCurrentStep('Your site is now live!');
      setPublishState('complete');
      setPublishedUrl(result.url);

      // Handle translation warnings
      if (result.hasTranslationWarnings && result.translationWarnings) {
        setTranslationWarnings(result.translationWarnings);
      }

    } catch (error) {
      // Always clear the interval on error
      clearInterval(progressInterval);

      console.error('Publishing failed:', error);
      setPublishState('error');
      setCurrentStep('Publishing failed');

      // Extract detailed error message, especially for security issues
      const errorData = error.response?.data;
      let errorMessage = 'An unexpected error occurred';

      if (errorData?.threats && errorData.threats.length > 0) {
        // Security/malware detection error
        const threatNames = errorData.threats.map(t => t.name || t).join(', ');
        errorMessage = `Security issue detected: ${threatNames}. Please remove the malicious code from your custom code section before publishing.`;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setProgress(0);
    }
  };

  const handleClose = () => {
    if (publishState === 'complete' || publishState === 'error') {
      setPublishState('idle');
      setProgress(0);
      setCurrentStep('');
      setError(null);
      setPublishedUrl(null);
      setTranslationWarnings([]);
      onClose();
    }
  };

  const getStepIcon = (stepKey) => {
    if (publishState === 'error') return <AlertCircle className="w-5 h-5 text-red-500" />;
    if (publishState === 'complete' && stepKey === 'complete') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (publishState === stepKey) return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
    
    const currentStepIndex = steps.findIndex(s => s.key === publishState);
    const stepIndex = steps.findIndex(s => s.key === stepKey);
    
    if (stepIndex < currentStepIndex) return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Globe className="w-5 h-5 mr-2 text-blue-500" />
            Publishing Site
          </h3>
          {(publishState === 'complete' || publishState === 'error') && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{currentStep}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                publishState === 'error' ? 'bg-red-500' : 
                publishState === 'complete' ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3 mb-6">
          {steps.map((step, index) => (
            <div key={step.key} className="flex items-center">
              {getStepIcon(step.key)}
              <span className={`ml-3 text-sm ${
                publishState === step.key ? 'text-blue-600 font-medium' :
                publishState === 'error' ? 'text-gray-400' :
                steps.findIndex(s => s.key === publishState) > index ? 'text-green-600' :
                'text-gray-500'
              }`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {publishState === 'error' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-red-800">Publishing Failed</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {publishState === 'complete' && publishedUrl && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">Site Published Successfully!</p>
                <a 
                  href={publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 mt-1 block"
                >
                  {publishedUrl}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Translation Warnings */}
        {publishState === 'complete' && translationWarnings.length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">Translation Updates Needed</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Some translations are out of date. Your site is live, but consider updating translations for the best experience.
                </p>
                <div className="mt-2 space-y-1">
                  {translationWarnings.map((warning, index) => (
                    <div key={index} className="text-xs text-yellow-700">
                      <span className="font-medium">{warning.language}</span>: {warning.pendingCount} of {warning.totalCount} sections need translation ({warning.percentage}% complete)
                    </div>
                  ))}
                </div>
                <p className="text-xs text-yellow-600 mt-2">
                  Use the Translation Manager in your site settings to update translations.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {publishState === 'complete' && publishedUrl && (
            <a
              href={publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 text-center"
            >
              View Site
            </a>
          )}
          
          {publishState === 'error' && (
            <button
              onClick={() => {
                setPublishState('idle');
                startPublishing();
              }}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Try Again
            </button>
          )}
          
          {(publishState === 'complete' || publishState === 'error') && (
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              Close
            </button>
          )}
        </div>

        {/* Multilingual Info */}
        {isMultilingual && publishState !== 'error' && (
          <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-600">
            <div className="flex items-center">
              <Globe className="w-3 h-3 mr-1" />
              Generating files for {languageCount + 1} languages
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishingModal;